import moment from 'moment';
import debug from 'debug';
import router from '@/router';
import globals from '@/globals';
import onboardingService from './onboarding.service';
import Logger from '../../lib/logger';
import eventService from '../../services/event';
import isMobile from '../../lib/isMobile';

const log = debug('store:onboarding');

function nextRoute(step) {
  if (step === 'WAITING' || step === 'FRONTDOC') {
    return 'onboarding-frontdoc-step';
  }
  if (step === 'BACKDOC') {
    return 'onboarding-backdoc-step';
  }
  if (step === 'SELFIE') {
    return 'onboarding-selfie-step';
  }
  if (step === 'INSURANCE') {
    return 'onboarding-insurance';
  }
  if (step === 'SIGNATURE') {
    return 'onboarding-signature-contract-view';
  }
  if (step === 'ENTRY' || step === 'COMPLETE' || step === 'DESK') {
    return 'onboarding-complete';
  }
  if (step === 'REFUSED') {
    return 'onboarding-denied';
  }
  return 'onboarding-error';
}

function afterLoadingRoute({
  status, step, downPayment, downPaymentPaid, documentSended,
}) {
  if (step === 'COMPLETE') {
    return 'onboarding-next-steps';
  }

  if (status === 'CANCELED') {
    return 'onboarding-denied';
  } if (!step) {
    return 'onboarding-denied';
  } if (step === 'COMPLETE') {
    return 'onboarding-approved';
  } if (step === 'REFUSED') {
    return 'onboarding-denied';
  } if (step === 'INSURANCE') {
    return 'onboarding-insurance';
  } if (step === 'SIGNATURE') {
    return 'onboarding-signature-contract-view';
  } if (documentSended && (!downPayment || downPaymentPaid)) {
    return 'onboarding-complete';
  }
  return 'onboarding-next-steps';
}

export default {
  namespaced: true,
  state: {
    isOnboardingTokenValid: true,
    onboardingId: null,
    transactionId: null,
    hasTrustedOnboarding: false,
    chosenConfirmationResendType: false,
    disableTheResendCounter: false,
    confirmationResendType: null,
    showResendOptions: false,
    iuguUser: null,
    onboardingToken: null,

    step: null,

    downPayment: null,
    downPaymentPaid: null,
    documentSended: null,
    downPaymentMethod: null,
    downPaymentPayDay: null,

    cardType: null,

    nextRoute: null,
    afterLoadingRoute: null,

    isMobile: isMobile(),

    name: '',
    phone: null,
    email: null,
    address: null,
    city: null,
    state: null,
    zipCode: null,
    birthdate: null,

    confirmationDevice: null,
    sendDocumentAt: null,
    bypassDocuments: false,

    createdAt: null,
    totalAmount: null,
    numberOfInstallments: null,
    paymentInstallments: null,
    interest: null,

    drawerEntrance: false,
    drawerDocument: false,
    drawerOrderDetails: false,
    drawerProposalDetails: false,

    orderId: null,
    barCodeNumber: null,
    payNumber: null,
    downPaymentValue: null,
    downPaymentLink: null,
    downPaymentDueDate: null,
    downPaymentPercentage: null,
    downPaymentPixAvailable: null,
    downPaymentPixCode: null,
    productName: '',
    productAmount: 0,
    originalImageUrl: '',
    isCreditCardAvailable: false,

    products: null,
    totalValue: null,
    cetPA: null,

    facetecErrorCount: 0,

    cameraOpened: false,
    selfieFlow: null,
    insuranceOptions: {},
  },
  actions: {
    async fetchInsuranceOptions({ commit, state }) {
      try {
        const { data } = await onboardingService
          .getInsuranceOptions(state.onboardingId);
        commit('setInsuranceOptions', data[0]);
      } catch (error) {
        Logger.error(error);
        throw new Error(error);
      }
    },
    async sendInsuranceConfirmation({ state }, hasInsurance) {
      try {
        await onboardingService
          .sendInsuranceConfirmation(state.onboardingId, hasInsurance);
      } catch (error) {
        Logger.error(error);
        throw new Error(error);
      }
    },
    async getOnboarding({ commit, dispatch, state }, { transactionId, facetecErrorCount }) {
      try {
        const { data } = await onboardingService
          .getOnboarding({ transactionId, isMobile: state.isMobile });
        const {
          onboarding,
          transaction,
          checkout,
        } = data;
        if (['OPEN', 'PENDING', 'CANCELED'].includes(transaction.status)) {
          commit('setOnboardingData', {
            onboarding,
            transaction,
            checkout,
            facetecErrorCount,
          });
        }
      } catch (error) {
        if (error && error.response && [401, 500].includes(error.response.status)) {
          commit('setIsOnboardingTokenValid', false);
          dispatch('auth/setToken', { token: null }, { root: true });
          router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          Logger.error(error);
          throw new Error(error);
        }
      }
    },
    async acceptContract({ state }) {
      await onboardingService.sendContractConfirmation({
        onboardingId: state.onboardingId,
      });
    },
    async createIuguUser({ commit }, { userId, transactionId, token }) {
      try {
        const { data } = await onboardingService.createIuguUser({ userId, transactionId, token });
        commit('setIuguUser', data);
        return data;
      } catch (error) {
        return error;
      }
    },
    async payWithCreditCard(_, { userId, transactionId, isPaymentMethodSaved, token }) {
      try {
        if (isPaymentMethodSaved) {
          const data = await onboardingService.payWithCreditCard(
            { userId, transactionId, isPaymentMethodSaved },
          );
          return data;
        }
        const data = await onboardingService.payWithCreditCard(
          { userId, transactionId, isPaymentMethodSaved, token },
        );
        return data;
      } catch (error) {
        return error;
      }
    },
    async resendConfirmation({ commit, dispatch, state }) {
      try {
        await onboardingService.resendConfirmation({
          onboardingId: state.onboardingId,
          isMobile: state.isMobile,
          type: state.confirmationResendType,
        });
      } catch (error) {
        if (error && error.response && error.response.status === 401) {
          commit('setIsOnboardingTokenValid', false);
          dispatch('auth/setToken', { token: null }, { root: true });
          router.replace({ name: 'login' }).catch((err) => log('router error', err));
        } else {
          Logger.error(error);
          throw new Error(error);
        }
      }
    },
    async sendConfirmationCode({ commit, dispatch, state }, { securityCode }) {
      try {
        const { data } = await onboardingService.sendConfirmation({
          onboardingId: state.onboardingId,
          securityCode,
        });

        const { onboarding } = data;

        return { success: true, onboarding };
      } catch (error) {
        if (error && error.response && error.response.status) {
          if (error.response.status === 401) {
            commit('setIsOnboardingTokenValid', false);
            dispatch('auth/setToken', { token: null }, { root: true });
            router.replace({ name: 'login' }).catch((err) => log('router error', err));
            return { success: false };
          }
          return { success: false };
        }
        Logger.error(error);
        throw new Error(error);
      }
    },
    async updateStep({ commit }, { step }) {
      commit('updateStep', { step });
    },
    changeDownPaymentMethod({ commit }, downPaymentMethod) {
      commit('setDownPaymentMethod', downPaymentMethod);
    },
    updateCreditCardPendingData({ commit }, cardType) {
      commit('setCreditCardPendingData', cardType);
    },
    onAlreadyPaid({ commit }) {
      commit('setDownPaymentPaid');
    },
    emitEvent({ rootState, state }, {
      type,
      page = 'onboarding',
    }) {
      const transaction = state.transaction || {};
      eventService.logOnboardingEvents({
        type,
        data: {
          ...state,
          apiKey: transaction.retailerId || null,
          page,
          retailerId: transaction.retailerId || null,
          checkoutId: transaction.checkoutId || null,
          user: {
            name: transaction.name || null,
            phone: transaction.phone || null,
            cpf: transaction.cpf || null,
            email: transaction.email || null,
            zipCode: transaction.zipCode || null,
            addressNumber: transaction.addressNumber || null,
            addressStreet: transaction.addressStreet || null,
            addressComplement: transaction.addressComplement || null,
            city: transaction.city || null,
            state: transaction.state || null,
          },
          productInfo: {
            name: transaction.productName || null,
            image: transaction.productImageUrl || null,
            url: transaction.productUrl || null,
            value: transaction.productPrice || null,
            amount: transaction.productAmount || null,
          },
          trackId: rootState.track && rootState.track.id,
        },
      }).catch((err) => log('err event', err));
    },
    setCameraOpened({ commit }, { opened }) {
      commit('setCameraOpened', { opened });
    },
    setShowResendOptions({ commit }, { showResendOptions }) {
      commit('setShowResendOptions', { showResendOptions });
    },
  },
  mutations: {
    incrementFacetecErrorCount(state) {
      state.facetecErrorCount += 1;
    },
    setInsuranceOptions(state, data) {
      state.insuranceOptions = data;
    },
    setIsOnboardingTokenValid(state, isValid) {
      state.isOnboardingTokenValid = isValid;
    },
    updateStep(state, { step }) {
      state.step = step;
      state.nextRoute = nextRoute(step);
      state.afterLoadingRoute = afterLoadingRoute({
        documentSended: state.documentSended,
        downPayment: state.downPayment,
        downPaymentPaid: state.downPaymentPaid,
        step,
      });
    },
    setOnboardingData(state, { onboarding, transaction, checkout, facetecErrorCount }) {
      log('onboarding', onboarding);
      log('transaction', transaction);
      state.transaction = transaction;

      const downPaymentBillet = (transaction && transaction.downPaymentBillet
        && transaction.downPaymentBillet) || {};

      state.isCreditCardAvailable = !!(downPaymentBillet.isCreditCardAvailable);
      state.downPayment = transaction && transaction.downPayment;
      state.downPaymentPaid = downPaymentBillet.paymentStatus === 'PAID';
      state.documentSended = !['WAITING', 'CONFIRMATION', 'FRONTDOC', 'BACKDOC', 'SELFIE']
        .includes(onboarding.step);

      state.step = onboarding.step;
      state.onboardingId = onboarding._id;
      state.onboardingToken = onboarding.onboardingToken;
      state.nextRoute = nextRoute(onboarding.step);
      state.selfieFlow = onboarding.selfieFlow || 'FACETEC';
      state.afterLoadingRoute = afterLoadingRoute({
        status: transaction && transaction.status,
        documentSended: state.documentSended,
        downPayment: state.downPayment,
        downPaymentPaid: state.downPaymentPaid,
        step: state.step,
      });
      state.bypassDocuments = onboarding.bypassDocuments;

      state.productAmount = transaction && transaction.productAmount;
      state.productName = transaction && transaction.productName;
      state.originalImageUrl = transaction && transaction.originalImageUrl;

      state.orderId = transaction && transaction.orderId;
      state.name = transaction && transaction.name;
      state.cpf = transaction && transaction.cpf;
      state.birthdate = checkout && checkout.birthdate;
      state.phone = transaction && transaction.phone;
      state.email = transaction && transaction.email;
      state.address = transaction && transaction.address;
      state.city = transaction && transaction.city;
      state.state = transaction && transaction.state;
      state.zipCode = transaction && transaction.zipCode;
      state.createdAt = transaction && transaction.createdAt;
      state.hasGracePeriod = transaction && transaction.hasGracePeriod;
      state.gracePeriodDueDate = transaction && transaction.gracePeriodDueDate;

      state.totalAmount = transaction && transaction.totalAmount;
      state.numberOfInstallments = transaction && transaction.numberOfInstallments;
      state.paymentInstallments = transaction && transaction.paymentInstallments;
      state.interest = transaction && transaction.interestRatePM;
      state.transactionDownPaymentValue = transaction && transaction.downPaymentValue;

      state.transactionId = transaction && transaction._id;
      state.barCodeNumber = downPaymentBillet.billetDetails
        && transaction.downPaymentBillet.billetDetails.barcodeNumber;
      state.payNumber = downPaymentBillet.payNumber;
      state.downPaymentValue = downPaymentBillet.value;
      state.downPaymentLink = downPaymentBillet.installmentLink;
      state.downPaymentDueDate = moment(downPaymentBillet.dueDate).format('DD/MM/YYYY');
      state.downPaymentPayDay = moment(downPaymentBillet.payday).format('DD/MM/YYYY') || '';
      state.downPaymentMethod = downPaymentBillet.paymentType || 'PIX';
      state.cardType = downPaymentBillet.cardType || '';
      state.downPaymentPixAvailable = downPaymentBillet.isPixAvailable;
      state.downPaymentPixCode = downPaymentBillet.pixCode;
      state.downPaymentPercentage = transaction && transaction.downPaymentPercentage;

      state.products = checkout && checkout.products;
      state.totalValue = checkout && checkout.totalValue;
      state.cetPA = transaction && transaction.cetPA;

      state.source = checkout && checkout.source;
      state.facetecErrorCount = facetecErrorCount ? parseInt(facetecErrorCount, 10) : 0;

      state.confirmationDevice = isMobile() ? 'mobile' : 'desktop';
      state.hasTrustedOnboarding = onboarding.hasTrustedOnboarding;
      state.sendDocumentAt = onboarding.steps
        && onboarding.steps.filter((step) => step.fromStep === 'SELFIE')[0]
        && onboarding.steps.filter((step) => step.fromStep === 'SELFIE')[0].updatedAt;
    },
    setDownPaymentMethod(state, downPaymentMethod) {
      state.downPaymentMethod = downPaymentMethod;
    },
    setDownPaymentPaid(state) {
      state.downPaymentPaid = true;
    },
    setCreditCardPendingData(state, cardType) {
      state.cardType = cardType;
      state.downPaymentPayDay = moment().format('DD/MM/YYYY');
    },
    setDrawerEntrance(state, drawerEntrance) {
      state.drawerEntrance = drawerEntrance;
    },
    setDrawerDocument(state, drawerDocument) {
      state.drawerDocument = drawerDocument;
    },
    setDrawerOrderDetails(state, drawerOrderDetails) {
      state.drawerOrderDetails = drawerOrderDetails;
    },
    setDrawerProposalDetails(state, drawerProposalDetails) {
      state.drawerProposalDetails = drawerProposalDetails;
    },
    setCameraOpened(state, { opened }) {
      state.cameraOpened = opened;
    },
    setChosenConfirmationResendType(state) {
      state.chosenConfirmationResendType = !state.chosenConfirmationResendType;
    },
    setDisableTheResendCounter(state) {
      state.disableTheResendCounter = !state.disableTheResendCounter;
    },
    setResendConfirmationType(state, newConfirmationResendType) {
      state.confirmationResendType = newConfirmationResendType;
    },
    setShowResendOptions(state, { showResendOptions }) {
      state.showResendOptions = showResendOptions;
    },
    setIuguUser(state, { iuguUser }) {
      state.iuguUser = iuguUser;
    },
  },
  getters: {
    getUserID(state) {
      return state.transaction.userId;
    },
    getIuguUser(state) {
      return state.iuguUser;
    },
    getBirthDate(state) {
      return state.birthdate;
    },
    getTransactionID(state) {
      return state.transactionId;
    },
    onboardingId(state) {
      return state.onboardingId;
    },
    onboardingOverviewData(state) {
      return {
        name: state.name,
        phone: state.phone,
        address: state.address,
        city: state.city,
        state: state.state,
        zipCode: state.zipCode,
        products: state.products,
        createdAt: state.createdAt,
        orderId: state.orderId,
      };
    },
    facetecErrorCount(state) {
      return state.facetecErrorCount;
    },
    insuranceOptions(state) {
      return state.insuranceOptions;
    },
    totalValue(state) {
      return state.totalValue;
    },
    ccbUrl(state) {
      const { transactionId } = state;
      const { apiUrl } = globals();

      return `${apiUrl}/public/ccb/transaction/${transactionId.toString()}`;
    },
  },
};
